import { Component, inject } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription, switchMap, map } from "rxjs";
import { SocketService } from "../../core/event/services/socket.service";
import { AlertService } from "../alert/services/alert.service";
import {
  AlertQueueService,
  AlertPlaybackControl,
} from "../alert/services/alert-queue.service";
import { AlertPlaybackOrchestrator } from "../alert/services/alert-playback-orchestrator.service";
import { HybridTTSService } from "../../core/services/hybrid-tts.service";
import { DonationAudioService } from "./services/donation-audio.service";
import { DonationTemplateService } from "./services/donation-template.service";
import { DatadogService } from "../../core/observability/datadog.service";
import { MusicthonService } from "../../core/musicthon/musicthon.service";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { AlertDisplayComponent } from "../alert/components/alert-display/alert-display.component";
import { v4 as uuidv4 } from "uuid";
import {
  HEARTBEAT_INTERVAL_MS,
  ALERT_DEFAULT_DURATION_MS,
  ALERT_AUTOHIDE_BUFFER_MS,
  ALERT_TIMEOUT_MS,
  ALERT_GUARD_MS,
} from "../alert/services/alert-constants";

enum EventType {
  DONATION = "donation",
  SALE = "sale",
  ALERT_SETTINGS_UPDATED = "alert_settings_updated",
  STOP_ALERT = "stop_alert",
  RELOAD_PAGE = "reload_page",
}

@Component({
  standalone: true,
  selector: "app-donation",
  templateUrl: "./donation.component.html",
  styleUrls: ["./donation.component.scss"],
  imports: [CommonModule, FormsModule, AlertDisplayComponent, MatIconModule],
  providers: [SocketService], // ✅ Removido HybridTTSService - usar singleton global
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0 })),
      transition(":enter", [animate("300ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("300ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class DonationComponent {
  constructor() {}

  public event: any = null;
  public showMessage = false;
  public message: string = "";

  // Parâmetros da URL
  public hashDonation!: string;
  public paramSubscriptions: Subscription[] = [];
  public streamerId: string = "";

  // Serviços
  alertService = inject(AlertService);
  alertQueueService = inject(AlertQueueService);
  socketService = inject(SocketService);
  activatedRoute = inject(ActivatedRoute);
  titleService = inject(Title);
  hybridTTSService = inject(HybridTTSService); // 🎯 Serviço híbrido de TTS
  alertAudioOrchestrator = inject(AlertPlaybackOrchestrator);
  donationAudioService = inject(DonationAudioService);
  donationTemplateService = inject(DonationTemplateService);
  datadogService = inject(DatadogService);
  musicthonService = inject(MusicthonService); // 🎵 Serviço para carregar config do Musicthon

  // TTS
  private speechSynthesis: SpeechSynthesis | null = null;

  // Flags
  public isDonation = false;
  public alertPosition: "top" | "center" | "bottom" = "center";
  public watchMode = false; // 👁️ Modo visualização (não envia ACK)

  // Armazenar evento atual para TTS
  private currentEventForTTS: any = null;

  // 🎯 Subject RxJS que emite quando coin.wav + TTS terminarem (um novo por alerta)
  private audioFinished$: Subject<void> | null = null;
  private stopRequested = false;
  private stopQueueSub?: Subscription;
  private heartbeatTimer?: any;
  private socketHealthSubs: Subscription[] = [];
  private displayId: string = this.getOrCreateDisplayId();

  // 🎯 TEMPLATE SEPARADO - Propriedades para nova estrutura visual
  public mainText: string = "";
  public userMessage: string = "";

  // 🔊 Volume do alerta (será preenchido pelas configurações)
  public alertVolume: number = 70; // Default 70%
  public stopSignal = 0; // usado para forçar parada do áudio coin.wav
  public voiceRecordingUrl: string | null = null; // URL do áudio customizado
  public musicthonConfig: any = null; // 🎵 Config do Musicthon para processar áudio iOS
  public currentDonationId: string = ''; // 🎵 ID da doação atual
  private alertsPaused = false;

  ngOnInit(): void {
    // 👁️ Detectar watch mode da rota
    this.activatedRoute.data.subscribe(data => {
      this.watchMode = data['watchMode'] || false;
      if (this.watchMode) {
        console.log('👁️ [Donation] MODO VISUALIZAÇÃO - Esperando broadcasts');
      }
    });

    this.initializeParams();
    this.initializeTTS();
    this.setupSocketHealthListeners();
    this.connectToSocket();
    
    // 🎯 Normal mode: processa fila | Watch mode: escuta broadcasts
    if (this.watchMode) {
      this.subscribeToBroadcasts();
    } else {
      this.subscribeToAlertQueue();
    }
    
    this.stopQueueSub = this.alertQueueService.stopSignal$.subscribe(() => {
      this.stopCurrentPlayback();
    });
  }

  /**
   * 📥 Escuta fila centralizada de alertas (NORMAL MODE)
   */
  private subscribeToAlertQueue(): void {
    this.alertQueueService.alertPlayback$.subscribe(({ event, control }) => {
      if (!this.alertQueueService.isCurrentGeneration(event)) {
        control.complete();
        return;
      }
      // Filtrar apenas eventos do tipo DONATION
      if (event.type === EventType.DONATION) {
        this.playAlert(event, control);
      }
    });
  }

  /**
   * 👁️ Escuta broadcasts de displays normais (WATCH MODE)
   */
  private subscribeToBroadcasts(): void {
    this.socketService.onEvent('alert_display_broadcast').subscribe((payload: any) => {
      console.log('📺 [Donation Watch] Broadcast recebido', payload);
      
      // Reconstruir evento a partir do broadcast
      const event = {
        id: payload.alertId,
        type: EventType.DONATION,
        data: payload.alertData,
        timestamp: payload.timestamp,
      };
      
      // Criar control mock (watch não envia ACK)
      const mockControl: AlertPlaybackControl = {
        complete: () => {
          console.log('👁️ [Donation Watch] Playback completo (mock)');
          this.showMessage = false;
          this.event = null;
        },
        skip: () => {
          console.log('👁️ [Donation Watch] Skip chamado (mock)');
          this.showMessage = false;
          this.event = null;
        },
      };
      
      this.playAlert(event, mockControl);
    });
  }

  ngOnDestroy(): void {
    this.paramSubscriptions.forEach((sub) => sub.unsubscribe());
    this.stopQueueSub?.unsubscribe();
    this.socketHealthSubs.forEach((sub) => sub.unsubscribe());
    this.socketService.disconnect();
    clearInterval(this.heartbeatTimer);
  }

  private initializeParams(): void {
    const paramSub = this.activatedRoute.params
      .pipe(
        switchMap((params: any) => {
          this.hashDonation = params?.id;
          this.titleService.setTitle("The Cool Rhyno - Donations");
          return this.activatedRoute.queryParams;
        }),
        map((queryParams: any) => {
          return queryParams;
        })
      )
      .subscribe();

    this.paramSubscriptions.push(paramSub);
  }

  private startHeartbeat(): void {
    if (!this.streamerId) return;
    clearInterval(this.heartbeatTimer);
    this.socketService.emitWithAck("display_heartbeat", {
      streamerId: this.streamerId,
      displayId: this.displayId,
      setActive: true,
    }).subscribe();
    this.heartbeatTimer = setInterval(() => {
      this.socketService.emitWithAck("display_heartbeat", {
        streamerId: this.streamerId,
        displayId: this.displayId,
      }).subscribe();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private setupSocketHealthListeners(): void {
    this.socketHealthSubs.push(
      this.socketService.onConnected$.subscribe(() => {
        this.alertQueueService.resume();
      })
    );
    this.socketHealthSubs.push(
      this.socketService.onDisconnected$.subscribe((reason) => {
        this.datadogService.logWarn('[Donation] Socket disconnected, pausing queue', {
          reason,
          streamerId: this.streamerId,
        });
        this.alertQueueService.pause();
        this.stopCurrentPlayback(true);
        this.datadogService.trackAction("socket_disconnected", {
          reason,
          type: "donation",
        });
      })
    );
  }

  private getOrCreateDisplayId(): string {
    const key = "alert_display_id";
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem(key, id);
    return id;
  }

  private initializeTTS(): void {
    if ("speechSynthesis" in window) {
      this.speechSynthesis = window.speechSynthesis;

      const loadVoices = () => {
        const voices = this.speechSynthesis!.getVoices();

        const settings = this.alertService.getDefaultSettings();

        // Forçar carregamento de vozes; seleção ocorre em playFallbackTTS
        voices.find((voice) => voice.voiceURI === settings.speechVoice);
      };

      if (this.speechSynthesis.getVoices().length > 0) {
        loadVoices();
      } else {
        this.speechSynthesis.addEventListener("voiceschanged", loadVoices);
      }
    }
  }

  private connectToSocket(): void {
    if (this.hashDonation) {
      this.socketService.connect();

      // Obter streamerId a partir do hash
      this.socketService
        .emitWithAck("get_streamer_by_hash", { hash: this.hashDonation })
        .subscribe((response: any) => {
          if (response && response.data) {
            this.streamerId = response.data.id;
            
            // 👁️ Watch mode não deve ser considerado display ativo
            if (!this.watchMode) {
              this.startHeartbeat();
              this.socketService.emitWithAck("display_ready", {
                streamerId: this.streamerId,
                displayId: this.displayId,
              }).subscribe();
              console.info("[Overlay][donation] display_ready sent", {
                streamerId: this.streamerId,
                displayId: this.displayId,
              });
            } else {
              console.info("[Overlay][donation] 👁️ Watch mode - Sem display_ready/heartbeat", {
                streamerId: this.streamerId,
              });
            }
            
            // Garante que a fila local está ativa após reconexão
            this.alertQueueService.resume();
            this.alertService.loadConfigFromSocket(response.data.alertSettings);
            this.syncAlertsPausedFromSettings(
              response.data.alertSettings,
              "initial_connect"
            );
            // 🎵 Carregar voiceRecordingUrl das settings
            this.updateVoiceRecordingUrl();
            
            // 🎵 Carregar configuração do Musicthon (useMobileGain)
            this.loadMusicthonConfig();
            
            this.datadogService.init(this.streamerId);
            this.datadogService.setViewName(
              `donations/${this.streamerId || this.hashDonation}`
            );

            // 🎵 Escutar eventos de configuração do Musicthon para processamento de áudio iOS
            this.socketService
              .onEvent(`streamer:${this.streamerId}:musicthon_config`)
              .subscribe((payload: any) => {
                console.log('🎵 [Donation] Recebeu musicthon_config', payload);
                this.musicthonConfig = payload; // Armazenar config localmente
                this.hybridTTSService.setMusicthonConfig(payload);
              });

            this.socketService
              .onEvent(`event:${this.streamerId}`)
              .subscribe((eventPayload: any) => {
                if (eventPayload.type === EventType.ALERT_SETTINGS_UPDATED) {
                  this.getStreamerIdFromHash(
                    this.hashDonation,
                    "settings_updated"
                  );
                } else if (eventPayload.type === EventType.RELOAD_PAGE) {
                  console.log("🔄 Página recarregada via evento RELOAD_PAGE");
                  window.location.reload();
                } else if (eventPayload.type === EventType.STOP_ALERT) {
                  // Para imediatamente o alerta atual sem ACK; backend já marcou como skipped
                  this.stopCurrentPlayback(true, undefined, true);
                }
              });

            this.socketService
              .onEvent(`streamer:${this.streamerId}:play_alert`)
              .subscribe((payload: any) => {
                console.info("[Overlay][donation] play_alert recebido", {
                  alertId: payload?.alertId,
                  type: payload?.type,
                  targetDisplayId: payload?.targetDisplayId,
                  forThisDisplay:
                    !payload?.targetDisplayId ||
                    payload?.targetDisplayId === this.displayId,
                });
                this.handlePlayAlert(payload);
              });

            // 🛑 Listener para STOP_ALERT (skip-current do admin)
            this.socketService
              .onEvent(`streamer:${this.streamerId}:stop_alert`)
              .subscribe((payload: any) => {
                console.info("🚨🚨🚨 [Overlay][donation] stop_alert recebido 🚨🚨🚨", {
                  alertId: payload?.alertId,
                  clearQueue: payload?.clearQueue,
                  timestamp: new Date().toISOString(),
                });
                // Para imediatamente o alerta atual sem ACK; backend já marcou como skipped
                this.stopCurrentPlayback(true, undefined, true);
                console.info("✅ [Overlay][donation] stopCurrentPlayback executado");
                
                if (payload?.clearQueue) {
                  this.alertQueueService.clearAll('backend_stop_with_clear');
                }
              });

            // 👁️ Listener para broadcasts de displays (watch mode)
            this.socketService
              .onEvent(`alert_display_broadcast`)
              .subscribe((payload: any) => {
                if (!this.watchMode) return; // Ignora se não estiver em watch mode
                
                console.info('👁️ [Donation] alert_display_broadcast recebido', {
                  alertId: payload?.alertId,
                  timestamp: payload?.timestamp,
                });
                
                // Processar o alerta recebido via broadcast
                this.handlePlayAlert({
                  alertId: payload.alertId,
                  type: EventType.DONATION,
                  payload: payload.alertData,
                  data: payload.alertData,
                });
              });
          }
        });

      // Escutar o evento unificado como enviado pelo backend
      // legado: manter somente RELOAD/SETTINGS via event:{streamerId}
    }
  }

  private getStreamerIdFromHash(hash: string, source: string = "manual"): void {
    this.socketService
      .emitWithAck("get_streamer_by_hash", { hash })
      .subscribe((response: any) => {
        if (response && response.data) {
          this.alertService.loadConfigFromSocket(response.data.alertSettings);
          this.syncAlertsPausedFromSettings(
            response.data.alertSettings,
            `settings_fetch:${source}`
          );
          // 🎵 Atualizar voiceRecordingUrl quando settings mudam
          this.updateVoiceRecordingUrl();
        }
      });
  }

  private syncAlertsPausedFromSettings(
    alertSettings: any[],
    source: string
  ): void {
    const donationSettings = alertSettings?.find(
      (s: any) => s.alertType === "DONATION"
    );
    if (typeof donationSettings?.alertsPaused === "boolean") {
      this.applyAlertsPaused(donationSettings.alertsPaused, source);
    }
  }

  private applyAlertsPaused(paused: boolean, source: string): void {
    if (paused === this.alertsPaused) return;
    this.alertsPaused = paused;
    if (paused) {
      // Pausa a fila e interrompe o alerta atual
      this.alertQueueService.pause();
      this.stopCurrentPlayback();
      this.alertQueueService.cancelCurrent();
      this.datadogService.trackAction("alerts_paused", {
        source,
        type: "donation",
      });
    } else {
      this.alertQueueService.resume();
      this.datadogService.trackAction("alerts_resumed", {
        source,
        type: "donation",
      });
    }
  }

  private updateVoiceRecordingUrl(): void {
    const settings = this.alertService.getSettings("donation");
    this.voiceRecordingUrl = settings.voiceRecordingUrl || null;
    console.log('🎵 [Donation] voiceRecordingUrl atualizado:', this.voiceRecordingUrl);
    console.log('🔍 [Donation] Settings completas:', settings);
  }

  private async loadMusicthonConfig(): Promise<void> {
    try {
      const config = await this.musicthonService.loadPublicConfig(this.hashDonation);
      if (config) {
        this.musicthonConfig = config;
        this.hybridTTSService.setMusicthonConfig(config);
        console.log('🎵 [Donation] Musicthon config carregada proativamente', { 
          useMobileGain: config.useMobileGain,
          streamerId: this.streamerId 
        });
      }
    } catch (error) {
      console.error('❌ [Donation] Erro ao carregar musicthon config:', error);
    }
  }

  private sendAck(alertId: string, status: "done" | "failed", reason?: string) {
    // 👁️ Não enviar ACK no modo visualização
    if (this.watchMode) {
      console.log(`ℹ️ [Donation] WATCH MODE - ACK suprimido para ${alertId} (${status})`);
      return;
    }

    this.socketService.emitWithAck("ack_alert", {
      alertId,
      status,
      reason,
    }).subscribe({
      next: () => {},
      error: (err) => {
        console.error("[Overlay][donation] ACK via WS falhou, tentando emit simples", err);
        this.socketService.emit("ack_alert", { alertId, status, reason });
      },
    });
  }

  private handlePlayAlert(payload: any): void {
    if (!payload || !payload.alertId) return;
    const type = String(payload.type || "").toLowerCase();
    if (type !== EventType.DONATION) {
      console.debug("[Overlay][donation] Ignorando play_alert: tipo diferente", payload);
      return;
    }
    if (payload.targetDisplayId && payload.targetDisplayId !== this.displayId) {
      console.debug("[Overlay][donation] Ignorando play_alert: targetDisplayId não corresponde", {
        payloadTarget: payload.targetDisplayId,
        displayId: this.displayId,
      });
      return;
    }
    const data = payload.payload || payload.data;
    const settings = this.alertService.getSettings("donation");
    const total = data?.total ?? data?.amount;
    if (total !== undefined && total < (settings as any).minAmount) {
      this.datadogService.logInfo('[Donation] Alert ignored: below minAmount', {
        alertId: payload.alertId,
        total,
        minAmount: (settings as any).minAmount,
      });
      this.socketService.emitWithAck("ack_alert", {
        alertId: payload.alertId,
        status: "done",
      }).subscribe();
      console.info("[Overlay][donation] Ignorando por minAmount, ack done", {
        alertId: payload.alertId,
        total,
      });
      return;
    }
    this.datadogService.logInfo('[Donation] Alert received and enqueueing', {
      alertId: payload.alertId,
      type,
      priority: payload.priority,
      amount: total,
    });
    console.info("[Overlay][donation] Enfileirando alerta", {
      alertId: payload.alertId,
      type,
      priority: payload.priority,
    });
    this.alertQueueService.enqueue({
      id: payload.alertId,
      type: EventType.DONATION,
      data,
      priority: payload.priority, // High priority (>= 999) bypasses pause
    });
  }

  /**
   * 🎬 Processa alerta individual (chamado pela fila): mostra UI, aguarda coin/TTS via orquestrador e sinaliza a fila.
   */
  private async playAlert(
    event: any,
    control: AlertPlaybackControl
  ): Promise<void> {
    // 🔄 Resetar flag de stop para permitir que este alerta toque
    this.hybridTTSService.resetStopFlag();
    
    this.stopRequested = false;
    let ackSent = false;
    let autoHideTimer: any;
    try {
      const settings = this.alertService.getSettings(event.type);

      // Configurar dados do alerta
      this.event = event;
      this.currentEventForTTS = event;
      this.isDonation = event.type === EventType.DONATION;
      this.currentDonationId = event.data?.id || ''; // 🎵 Armazenar donation ID
      this.alertVolume = (settings.speechVolume || 0.8) * 100;
      this.alertPosition = settings.alertPosition;

      // 🔄 Criar novo Subject para este alerta específico
      this.audioFinished$ = new Subject<void>();
      const coinFinished$ = this.audioFinished$;
      this.datadogService.logInfo("alert_start", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
      });

      // Gerar template visual via serviço dedicado
      const template = this.isDonation
        ? this.donationTemplateService.buildDonationTemplate(event.data)
        : this.donationTemplateService.buildSaleTemplate(event.data);
      this.mainText = template.mainText;
      this.userMessage = template.userMessage;

      // Mostrar alerta
      this.showMessage = true;

      // 📺 Broadcast para watch displays DEPOIS de exibir (apenas se não estiver em watch mode)
      if (!this.watchMode && this.streamerId) {
        this.socketService.emitWithAck('broadcast_alert_display', {
          streamerId: this.streamerId,
          alertId: event.id,
          alertData: event.data,
        }).subscribe({
          next: () => console.log('📺 [Donation] Broadcast enviado para watches'),
          error: (err) => console.error('❌ [Donation] Erro ao enviar broadcast', err),
        });
      }

      // Fallback de auto-hide para evitar travar UI se áudio falhar/autoplay bloquear
      const configuredDurationMs = (settings as any).alertDuration
        ? Number((settings as any).alertDuration) * 1000
        : 0;
      const baseDuration = Math.max(
        configuredDurationMs,
        ALERT_TIMEOUT_MS
      );
      const maxMs = baseDuration + ALERT_AUTOHIDE_BUFFER_MS;
      autoHideTimer = setTimeout(() => {
        if (ackSent) return;
        this.stopRequested = true;
        this.showMessage = false;
        this.event = null;
        this.currentEventForTTS = null;
        this.isDonation = false;
        this.currentDonationId = '';
        this.mainText = "";
        this.userMessage = "";
        this.audioFinished$ = null;
        control.complete();
        this.socketService.emitWithAck("ack_alert", {
          alertId: event.id,
          status: "done",
        }).subscribe();
        ackSent = true;
      }, maxMs);

      // ✅ Orquestrar coin + TTS + timeout via serviço
      await this.alertAudioOrchestrator.runFlow({
        eventId: event.id,
        waitForCoin$: coinFinished$,
        guardMs: ALERT_GUARD_MS,
        timeoutMs: ALERT_TIMEOUT_MS,
        onTimeout: () => this.stopCurrentPlayback(false, event.id),
        playTTS: async () => {
          if (this.stopRequested) return;
          await this.donationAudioService.play(event, this.streamerId, () =>
            this.playFallbackTTS(event)
          );
        },
      });
      if (this.stopRequested) {
        clearTimeout(autoHideTimer);
        control.complete();
        return;
      }
      this.datadogService.logInfo("alert_finished", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
      });

      // Esconder e limpar
      this.showMessage = false;
      this.event = null;
      this.currentEventForTTS = null;
      this.isDonation = false;
      this.currentDonationId = '';
      this.mainText = "";
      this.userMessage = "";
      this.audioFinished$ = null;

      // ✅ Avisar fila que terminou
      control.complete();
      this.sendAck(event.id, "done");
      ackSent = true;
    } catch (error) {
      console.error("❌ Erro ao processar alerta:", error);
      this.datadogService.logError("alert_playback_error", {
        eventId: event.id,
        type: event.type,
        streamerId: this.streamerId,
        error: String(error),
      });
      
      this.stopRequested = true;
      this.showMessage = false;
      this.event = null;
      this.currentEventForTTS = null;
      this.isDonation = false;
      this.currentDonationId = '';
      this.mainText = "";
      this.userMessage = "";
      this.audioFinished$ = null;
      control.complete();
      
      // ❌ Enviar como "failed" para poder retry depois
      this.sendAck(event.id, "failed", "audio_error");
      ackSent = true;
    } finally {
      if (ackSent) {
        clearTimeout(autoHideTimer);
      }
    }
  }

  /**
   * 🧹 Remover códigos SSML/ElevenLabs que o TTS nativo não entende
   */
  private cleanSSMLTags(text: string): string {
    if (!text) return text;

    // Remove tags <break time="..."/> usadas pelo ElevenLabs
    return text.replace(/<break\s+time="[^"]*"\s*\/>/gi, "");
  }

  /**
   * � Remove URLs do texto e substitui por "link" para TTS
   * O texto original permanece intacto na exibição visual
   */
  private sanitizeForTTS(text: string): string {
    if (!text) return text;

    // Substituir URLs completas (http/https) por "link"
    let sanitized = text.replace(/https?:\/\/[^\s]+/gi, "link");

    // Substituir www.* por "link"
    sanitized = sanitized.replace(/www\.[^\s]+/gi, "link");

    // Remover múltiplos "link link" seguidos
    sanitized = sanitized.replace(/\blink(\s+link)+\b/gi, "link");

    // Limpar espaços duplos
    sanitized = sanitized.replace(/\s+/g, " ").trim();

    return sanitized;
  }

  /**
   * 🔄 TTS tradicional (fallback)
   */
  private async playFallbackTTS(event: any): Promise<void> {
    console.log('🎤 [Donation] playFallbackTTS() iniciado', { event, message: this.message });
    
    return new Promise<void>((resolve) => {
      const settings = this.alertService.getSettings(event.type);

      if (!this.speechSynthesis) {
        console.warn("⚠️ Speech synthesis não disponível");
        resolve();
        return;
      }

      const voices = this.speechSynthesis.getVoices();
      
      console.log('🎤 [Donation] Vozes disponíveis:', voices.length);

      const selectedVoice =
        voices.find((voice) => voice.voiceURI === settings.speechVoice) ||
        voices.find((voice) => voice.lang.startsWith("pt")) ||
        voices[0] ||
        null;
      
      console.log('🎤 [Donation] Voz selecionada:', selectedVoice?.name);

      // 🎯 Construir mensagem a partir dos dados do evento (para testes ou quando this.message está vazio)
      let messageText = this.message;
      if (!messageText && event.data) {
        const username = event.data.username || 'Anônimo';
        const amount = event.data.amount || 0;
        const userMessage = event.data.message || '';
        messageText = `${username} doou ${amount}. ${userMessage}`;
        console.log('🎤 [Donation] Mensagem construída a partir dos dados do evento');
      }

      // 🧹 Limpar códigos SSML antes de passar para o TTS nativo
      let cleanedMessage = this.cleanSSMLTags(messageText);

      // 🔗 Remover URLs e substituir por "link"
      cleanedMessage = this.sanitizeForTTS(cleanedMessage);
      
      console.log('🎤 [Donation] Texto limpo para TTS:', cleanedMessage);

      const utterance = new SpeechSynthesisUtterance(cleanedMessage);
      utterance.lang = selectedVoice?.lang || "pt-BR";
      utterance.voice = selectedVoice;
      utterance.rate = settings.speechRate;
      utterance.pitch = settings.speechPitch;
      // 🔊 settings.speechVolume já vem como 0-1 do backend
      utterance.volume = settings.speechVolume || 0.8;

      // ✅ Resolver Promise quando TTS terminar
      utterance.onend = () => {
        console.log('✅ [Donation] TTS finalizado');
        resolve();
      };
      utterance.onerror = (error) => {
        console.warn("⚠️ TTS error:", error);
        resolve();
      };

      this.speechSynthesis.cancel();
      console.log('🔊 [Donation] Iniciando speechSynthesis.speak()');
      this.speechSynthesis.speak(utterance);
    });
  }

  public async onAudioFinished(
    reason: string = "audio-finished"
  ): Promise<void> {
    console.log(
      "🎵 [Donation] onAudioFinished chamado. currentEventForTTS:",
      this.currentEventForTTS?.id,
      "motivo:",
      reason
    );
    this.audioFinished$?.next();
  }

  /**
   * ⚡ Força parada do alerta atual (coin.wav + TTS) e, opcionalmente, limpa a fila.
   */
  public stopCurrentPlayback(
    clearQueue: boolean = false,
    targetEventId?: string,
    suppressAck: boolean = false
  ): void {
    this.datadogService.logInfo('[Donation] Stopping current playback', {
      clearQueue,
      targetEventId,
      suppressAck,
      currentEventId: this.currentEventForTTS?.id,
      streamerId: this.streamerId,
    });
    
    console.log('🛑 [Donation] stopCurrentPlayback chamado', {
      clearQueue,
      targetEventId,
      suppressAck,
      currentEventId: this.currentEventForTTS?.id,
      hasSpeechSynthesis: !!this.speechSynthesis,
    });
    
    this.stopRequested = true;
    const currentId = this.currentEventForTTS?.id;
    // Se houver um targetEventId e o alerta atual já mudou ou não existe, não interrompa o novo alerta.
    if (targetEventId) {
      if (
        !this.currentEventForTTS ||
        this.currentEventForTTS.id !== targetEventId
      ) {
        this.datadogService.logInfo('[Donation] Stop aborted: targetEventId mismatch', {
          targetEventId,
          currentEventId: this.currentEventForTTS?.id,
        });
        console.log('⚠️ [Donation] targetEventId não corresponde, abortando stop');
        return;
      }
    }

    // Para áudio coin.wav no AlertDisplay
    this.stopSignal++;
    console.log('🔊 [Donation] stopSignal incrementado:', this.stopSignal);

    // Para TTS em andamento
    if (this.speechSynthesis) {
      console.log('🎬 [Donation] Cancelando speechSynthesis');
      this.speechSynthesis.cancel();
    }

    // Parar áudio da IA (hybridTTS)
    console.log('🤖 [Donation] Parando hybridTTSService');
    this.hybridTTSService.stopCurrentAudio();
    
    console.log('🎧 [Donation] Parando alertAudioOrchestrator');
    this.alertAudioOrchestrator.stop();

    // Emitir no Subject para liberar fila se estiver aguardando
    this.audioFinished$?.next();
    this.audioFinished$ = null;

    // Limpar estado atual do alerta
    this.showMessage = false;
    this.event = null;
    this.currentEventForTTS = null;
    this.isDonation = false;
    this.currentDonationId = '';
    this.mainText = "";
    this.userMessage = "";

    if (clearQueue) {
      this.alertQueueService.cancelCurrent();
    }

    if (!suppressAck && currentId) {
      this.sendAck(currentId, "failed");
    }
  }
}

